import { DateTime } from 'luxon';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/button';
import { Badge, Card, CardBody, CardHeader, InlineRow, Muted, Stack } from '../../components/ui';
import { useWorkerStrikesQuery } from '../../services/serviceService/__generated__/queries.generated';
import { strikePayload, strikeRowState, type StrikeState } from '../ServiceJobPage/jobTimeline';
import { providerJobPath } from './ProviderJobCard';
import { StrikeAppealModal } from './StrikeAppealModal';

const TYPE_LABELS: Record<string, string> = {
  LATE_CANCEL: 'Late cancellation',
  NO_SHOW: 'No-show',
};

export const STRIKE_STATE_VIEW: Record<
  StrikeState,
  { label: string; tone: 'warning' | 'info' | 'success' | 'error' }
> = {
  open: { label: 'Counts against the worker', tone: 'warning' },
  appealed: { label: 'Appeal under review', tone: 'info' },
  removed: { label: 'Removed', tone: 'success' },
  upheld: { label: 'Upheld', tone: 'error' },
};

/**
 * One worker's late cancellations and no-shows of the last 90 days (spec 019
 * W7, D17), from `workerStrikes`, with Appeal on the open ones.
 */
export function WorkerStrikesCard({ workerCarId }: { workerCarId: string }) {
  const { data, loading } = useWorkerStrikesQuery({
    variables: { workerCarId },
    fetchPolicy: 'cache-and-network',
  });
  const [appealing, setAppealing] = useState<{ id: string; label: string } | null>(null);
  const strikes = data?.workerStrikes ?? [];

  return (
    <Card data-testid="worker-strikes">
      <CardHeader
        title="Strikes"
        description="Late cancellations and no-shows of the last 90 days. Too many pause the worker."
      />
      <CardBody>
        {loading && !data ? (
          <Muted $size="sm">Loading…</Muted>
        ) : strikes.length === 0 ? (
          <Muted $size="sm">No strikes. Nice work.</Muted>
        ) : (
          <Stack $gap={4}>
            {strikes.map(strike => {
              const state = strikeRowState(strike);
              const view = STRIKE_STATE_VIEW[state];
              const label = TYPE_LABELS[strike.type] ?? strike.type;
              const { bookingId } = strikePayload(strike.payload);
              return (
                <Stack key={strike.id} $gap={4}>
                  <InlineRow style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <span>
                      {label} ·{' '}
                      {DateTime.fromISO(strike.capturedAt ?? strike.receivedAt).toFormat(
                        'LLL d, yyyy',
                      )}
                      {bookingId && (
                        <>
                          {' '}
                          · <Link to={providerJobPath(bookingId)}>Job</Link>
                        </>
                      )}
                    </span>
                    <InlineRow>
                      <Badge tone={view.tone}>{view.label}</Badge>
                      {state === 'open' && (
                        <Button
                          type="button"
                          theme="secondary"
                          size="sm"
                          text="Appeal"
                          onClick={() => setAppealing({ id: strike.id, label })}
                          dataTestId={`strike-appeal-${strike.id}`}
                        />
                      )}
                    </InlineRow>
                  </InlineRow>
                </Stack>
              );
            })}
          </Stack>
        )}
      </CardBody>
      {appealing && (
        <StrikeAppealModal
          eventId={appealing.id}
          label={appealing.label}
          onClose={() => setAppealing(null)}
        />
      )}
    </Card>
  );
}
